//pages/TermsOfUse.tsx
import Layout from "@/components/Layout";
// SEO: Import SEO component for terms page metadata.
import SEO from '@/components/SEO';
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Mail } from "lucide-react";

export default function TermsOfUse() {
  const termsSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Terms of Use',
    'url': 'https://tradesagepro.com/terms',
    'description': 'Terms of Use governing access to TradeSagePro AI market research and analysis platform.',
    'about': { '@type': 'Organization', 'name': 'TradeSagePro' }
  };
  return (
    <Layout>
      {/* SEO TAGS: Terms page indexed for brand + compliance queries; provides WebPage schema. */}
      <SEO
        title="Terms of Use | TradeSagePro AI Market Research"
        description="Read the Terms of Use governing access, acceptable behavior, payments, security and liability for TradeSagePro AI market research and analysis platform."
        canonical="https://tradesagepro.com/terms"
        type="article"
        jsonLd={termsSchema}
      />
      {/* END SEO TAGS */}
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <FileText className="w-12 h-12 text-[hsl(193,100%,50%)] mr-3" />
              <h1 className="text-4xl font-bold text-white">Terms of Use</h1>
            </div>
            <p className="text-sm text-[hsl(210,9%,80%)]">Last Updated: October 12, 2025</p>
          </div>

          <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
            <CardContent className="p-8 max-w-none">
              <div className="space-y-10 text-[hsl(210,9%,91%)] leading-relaxed">

                <section id="welcome">
                  <p>
                    Welcome to JINOVEX LLC ("Company", "we", "our", or "us"). These Terms of Use ("Terms") govern your
                    access to and use of <span className="text-[hsl(193,100%,50%)]">www.tradesagepro.com</span> (the "Service").
                  </p>
                  <p className="mt-3">
                    By accessing or using the Service, you agree to comply with and be bound by these Terms. If you do not agree,
                    please discontinue use of the Service immediately.
                  </p>
                </section>

                <section id="service-disclaimer">
                  <h2 className="text-2xl font-bold text-white mb-3">1. Nature of the Service and Disclaimer</h2>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-white">Informational Purpose Only:</p>
                      <p>
                        The Service provides AI-generated market research and analysis outputs (e.g., Data Points for Position Initiation, Risk Management Parameters, Target Data Points for Position Closure, and No Trade outcomes) and related analytical reasoning for informational and educational purposes only.
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-white">No Guarantee of Performance:</p>
                      <p>
                        We do not guarantee any particular outcome, financial result, or profitability. You acknowledge that
                        markets involve substantial risks and that analysis outputs may not reflect future performance.
                      </p>
                    </div>
                  </div>
                </section>

                <section id="no-financial-advice">
                  <h2 className="text-2xl font-bold text-white mb-3">2. No Financial Advice</h2>
                  <p>
                    TradeSagePro is not a financial advisor, broker, or investment firm. The platform provides market research and analysis outputs for informational and educational purposes only and does not provide personalized advice or recommendations. Any decisions you make based on the information or analysis provided are your sole responsibility. You should conduct your own independent research and consider seeking advice from a qualified professional before making financial decisions.
                  </p>
                </section>

                <section id="risk-disclosure">
                  <h2 className="text-2xl font-bold text-white mb-3">3. Risk Disclosure</h2>
                  <p>
                    Financial markets are volatile and involve significant risk. Past performance, backtested reliability scores, model validation metrics, and historical concordance rates do not guarantee future results. Analysis outputs may be incomplete, delayed, or inaccurate due to data limitations or market conditions. You assume full responsibility for any financial decisions and outcomes, including potential losses, when using the Service.
                  </p>
                </section>

                <section id="accounts-security">
                  <h2 className="text-2xl font-bold text-white mb-3">4. Account Registration and Security</h2>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-white">Eligibility:</p>
                      <p>You must be at least 18 years old to use the Service. By registering, you represent that all information you provide is true and accurate.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Account Verification:</p>
                      <p>Registration requires a valid email address. You will receive a confirmation code to verify your account before accessing the Service.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Security Monitoring:</p>
                      <p>For security and fraud prevention purposes, we collect and monitor your public IP address, session data, and device information. If a login attempt is detected from a new or unrecognized device, you will be required to re-verify via email confirmation.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Account Responsibility:</p>
                      <p>You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.</p>
                    </div>
                  </div>
                </section>

                <section id="payments-credits-refunds">
                  <h2 className="text-2xl font-bold text-white mb-3">5. Payments, Tokens, and Refunds</h2>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-white">Payment Processing:</p>
                      <p>All transactions are securely processed via Paddle.com, a third-party PCI DSS–compliant payment processor.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Token System:</p>
                      <p>Users may purchase non-refundable tokens, which can be used to access AI market research and analysis services within the platform.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white">No Trade Outcome:</p>
                      <p>If an AI analysis results in a “No Trade” outcome, the tokens used for that analysis are automatically returned to your in-app balance.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Refund Policy:</p>
                      <ul className="list-disc pl-6 mt-1 space-y-1">
                        <li>Returned tokens do not constitute a monetary refund.</li>
                        <li>Monetary refunds are not provided, except as required by applicable consumer protection laws.</li>
                        <li>You acknowledge that token purchases are final and non-transferable.</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section id="intellectual-property">
                  <h2 className="text-2xl font-bold text-white mb-3">4. Intellectual Property</h2>
                  <p>
                    All platform content—including AI models, algorithms, software, databases, design elements, and analytical outputs—is
                    the exclusive property of JINOVEX LLC or its licensors. You may not copy, modify, distribute, reverse-engineer, or
                    exploit any part of the Service without our written consent. The TradeSagePro name and logo are protected trademarks.
                  </p>
                </section>

                <section id="user-conduct">
                  <h2 className="text-2xl font-bold text-white mb-3">6. User Conduct</h2>
                  <p className="mb-2">By using the Service, you agree that you will not:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Attempt to access another user’s account or data;</li>
                    <li>Interfere with the operation or security of the platform;</li>
                    <li>Use automated tools (bots, scrapers) to extract data;</li>
                    <li>Reproduce or publish AI outputs without attribution or authorization.</li>
                  </ul>
                  <p className="mt-2 text-sm text-[hsl(210,9%,80%)]">Violation of these terms may result in suspension or permanent termination of your account.</p>
                </section>

                <section id="limitation-liability">
                  <h2 className="text-2xl font-bold text-white mb-3">7. Limitation of Liability</h2>
                  <p>To the maximum extent permitted by law:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>The Service is provided “AS IS” and “AS AVAILABLE”, without warranties of any kind.</li>
                    <li>
                      JINOVEX LLC and its affiliates shall not be liable for any direct, indirect, incidental, or consequential damages—including
                      financial losses—arising from:
                      <ul className="list-disc pl-6 mt-1 space-y-1">
                        <li>reliance on AI-generated analysis outputs or market research;</li>
                        <li>platform downtime, errors, or unauthorized access;</li>
                        <li>loss of data, profits, or capital.</li>
                      </ul>
                    </li>
                  </ul>
                  <p className="mt-2">You assume full responsibility for your use of the Service and its analysis outputs.</p>
                </section>

                <section id="privacy">
                  <h2 className="text-2xl font-bold text-white mb-3">8. Privacy and Data Protection</h2>
                  <p>
                    Your use of the Service is also governed by our Privacy Policy, which complies with GDPR, CCPA/CPRA, PIPEDA, and LGPD
                    standards. By using the Service, you consent to the collection and processing of your personal data (such as email, IP
                    address, and device information) as described in that Policy. Refer to the Privacy Policy for details on your rights and
                    our data handling practices.
                  </p>
                </section>

                <section id="termination">
                  <h2 className="text-2xl font-bold text-white mb-3">9. Termination</h2>
                  <p>
                    We may suspend or terminate your account at any time, without notice, if: (i) you breach these Terms, (ii) you engage in
                    fraud or misuse of the platform, or (iii) required by law or regulatory order. Upon termination, any remaining tokens may
                    be forfeited without refund.
                  </p>
                </section>

                <section id="governing-law">
                  <h2 className="text-2xl font-bold text-white mb-3">10. Governing Law and Dispute Resolution</h2>
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of England and Wales (or your designated
                    jurisdiction if otherwise stated). Any dispute arising out of or relating to these Terms shall be subject to the exclusive
                    jurisdiction of competent courts in London, United Kingdom. You waive any right to participate in class actions against the
                    Company.
                  </p>
                </section>

                <section id="changes">
                  <h2 className="text-2xl font-bold text-white mb-3">11. Changes to These Terms</h2>
                  <p>
                    We may modify these Terms from time to time to reflect operational, legal, or regulatory changes. Updated versions will be
                    posted on the Service with the “Last Updated” date revised accordingly. Continued use of the Service after such updates
                    constitutes your acceptance.
                  </p>
                </section>

                <section id="contact">
                  <h2 className="text-2xl font-bold text-white mb-3">12. Contact Us</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="font-semibold text-white mb-1 flex items-center gap-2"><Mail className="w-4 h-4" /> Email</div>
                      <a className="text-[hsl(193,100%,50%)] hover:underline" href="mailto:info@tradesagepro.com">info@tradesagepro.com</a>
                    </div>
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="font-semibold text-white mb-1">Registered Address</div>
                      <div className="text-[hsl(210,9%,85%)]">JINOVEX LLC – Online Service Operations, Tbilisi, Georgia (operating globally)</div>
                    </div>
                  </div>
                </section>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
